/**
 * The token contract.
 *
 * Every colour this module names is a CSS custom property defined in
 * `tokens.css`, never a literal. A literal would be correct in one theme and
 * silently wrong in the other, and the wrongness would only show up on a
 * reviewer's laptop set the other way round.
 *
 * The period swatches are categorical and user-chosen; utilisation is a measured scale. They
 * never share a ramp.
 */

export type ThemeName = 'dark' | 'light';

/** Where a theme choice the user made explicitly is remembered. */
export const THEME_STORAGE_KEY = 'socialsrc.theme';

/** The collection-period swatches (`--period-<hue>-<shade>`): seven hues by three shades. A
 *  stored swatch is the key `<hue>.<shade>`, never a colour, so it repaints when the theme flips. */
export const PERIOD_HUES = ['red', 'orange', 'yellow', 'green', 'blue', 'purple', 'gray'] as const;
export const PERIOD_SHADES = ['pastel', 'normal', 'dark'] as const;
export type PeriodHue = (typeof PERIOD_HUES)[number];
export type PeriodShade = (typeof PERIOD_SHADES)[number];

/**
 * Utilisation bands, keyed to how far above the perfectly batched floor a
 * segment is actually paying.
 *
 * §6.6 defines `utilisation = floor / per_item`, so the ratio is the reciprocal
 * of the multiple of the floor being paid: 1.0 is perfectly batched, 0.5 is
 * twice the floor, 0.25 is four times it. The thresholds below are those two
 * multiples rather than even thirds, because "you are paying four times the
 * floor" is a sentence a tenant can act on and "you are in the bottom third"
 * is not.
 */
const WITHIN_TWICE_THE_FLOOR = 0.5;
const WITHIN_FOUR_TIMES_THE_FLOOR = 0.25;

function utilisationBand(ratio: number): 'low' | 'mid' | 'high' {
  // A ratio that is not a usable number reads as the worst band rather than
  // the reassuring one. A missing measurement must never render as "efficient".
  if (!Number.isFinite(ratio) || ratio < WITHIN_FOUR_TIMES_THE_FLOOR) return 'low';
  if (ratio < WITHIN_TWICE_THE_FLOOR) return 'mid';
  return 'high';
}

/** Utilisation is its own scale, never a period swatch. */
export function utilisationToken(ratio: number): string {
  return `var(--util-${utilisationBand(ratio)})`;
}

/** A value the estimate reported as measured renders differently from one
 *  that fell back to a default (§6.6, §17.4). Exactly the three members of
 *  `InputSource` in core's `estimate_burn_rate.py`: measured from L1, a
 *  platform default, or a figure the tenant supplied. Only `default` is an
 *  invitation to correct; rendering the other two the same way invites an
 *  edit that changes nothing. */
export type SourceKind = 'measured' | 'default' | 'tenant';

export function sourceToken(kind: SourceKind): string {
  return `var(--source-${kind})`;
}

/**
 * Pin the theme on an element, usually `document.documentElement`.
 *
 * The attribute is what `tokens.css` keys its explicit-choice blocks on, and it
 * wins over the system preference in both directions: `light` suppresses the
 * `prefers-color-scheme: dark` block, `dark` applies the dark palette on a
 * system set to light.
 */
export function applyTheme(root: HTMLElement, theme: ThemeName): void {
  root.setAttribute('data-theme', theme);
}

/**
 * Decide which theme to render, given whatever was stored and what the system
 * asks for.
 *
 * A stored value is whatever came out of storage, which is to say untrusted: an
 * older build's vocabulary, a hand-edited entry, or a string from another
 * application sharing the origin. Anything that is not one of the two themes is
 * ignored rather than passed through, because passing it through would set
 * `data-theme` to a value no block matches and leave the page on the light
 * palette regardless of the system preference.
 */
export function resolveTheme(stored: string | null, prefersDark: boolean): ThemeName {
  if (stored === 'dark' || stored === 'light') return stored;
  return prefersDark ? 'dark' : 'light';
}
